/**
 * Ammo Registry
 *
 * The per-kind registry for ammo types, in the same shape as the consumable
 * registry. The server populates it at boot from `config/ammo.json`; the
 * client does not need it (pickups arrive with their graphic and the HUD reads
 * counts from the player's pouch).
 */

import { Ammo } from './Ammo.js';
import { getSharedLogger } from './utils/loggerProxy.js';

const logger = getSharedLogger('GENERAL');

class AmmoRegistry {
  constructor() {
    /** @type {Map<string, Ammo>} */
    this.types = new Map();
  }

  /** @returns {Ammo|null} */
  register(ammoOrConfig) {
    if (!ammoOrConfig?.id) {
      logger.warn('[ammoRegistry] Refusing an ammo type with no id');
      return null;
    }
    const ammo = ammoOrConfig instanceof Ammo ? ammoOrConfig : new Ammo(ammoOrConfig);
    this.types.set(ammo.id, ammo);
    return ammo;
  }

  /** @returns {number} how many registered */
  registerBulk(configs) {
    if (!Array.isArray(configs)) return 0;
    return configs.reduce((count, config) => count + (this.register(config) ? 1 : 0), 0);
  }

  /** @returns {Ammo|null} */
  get(id) {
    return this.types.get(id) || null;
  }

  has(id) {
    return this.types.has(id);
  }

  /** @returns {Ammo[]} */
  getAll() {
    return Array.from(this.types.values());
  }
}

// Populated by the server at boot (src/server/runtime/registries.js).
const ammoRegistry = new AmmoRegistry();

export { AmmoRegistry, ammoRegistry };
